// --- src/components/Block.js ---
import React, { useEffect, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import { Toolbar } from "./Toolbar";
import { useAppContext } from '../AppContext';
import { Eye, PlusCircle, Send, PencilLine, ExternalLink } from 'lucide-react';

const Block = ({ block, renderChildren, setBlockRef, blockRefs, renderingContext = "default", referencingBlockId = null }) => {
  const { getArtifactById, findBlockById, navigateToBlock, highlightedBlockId, setHighlightedBlockId } = useAppContext();
  const elementRef = useRef(null);

  // --- ID and Ref Handling (No changes) ---
  const getFullBlockId = useCallback((currentBlock) => {
    if (!currentBlock) return null;
    return renderingContext === "reference"
      ? `ref-${referencingBlockId}-${currentBlock.id}`
      : `block-${currentBlock.id}`;
  }, [renderingContext, referencingBlockId]);

  const handleSetRef = useCallback((node) => {
    elementRef.current = node;
    const currentFullBlockId = getFullBlockId(block);
    if (setBlockRef && currentFullBlockId) {
      setBlockRef(node, currentFullBlockId, renderingContext);
    }
  }, [block, setBlockRef, renderingContext, getFullBlockId]);

  // --- Highlight Animation Handling (No changes) ---
  useEffect(() => {
    const element = elementRef.current;
    const currentFullBlockId = getFullBlockId(block);
    const isBlinking = highlightedBlockId === currentFullBlockId;

    if (isBlinking && element) {
      const handleAnimationEnd = () => {
        if (highlightedBlockId === currentFullBlockId) {
          setHighlightedBlockId(null);
        }
      };
      element.addEventListener('animationend', handleAnimationEnd, { once: true });
      return () => {
        element.removeEventListener('animationend', handleAnimationEnd);
      };
    }
  }, [block, highlightedBlockId, setHighlightedBlockId, getFullBlockId]);


  // --- Early Exit ---
  if (!block) {
    return <div className="text-sm text-gray-500">Block data missing or invalid.</div>;
  }

  // --- Constants ---
  const fullBlockId = getFullBlockId(block);
  const shouldBlink = highlightedBlockId === fullBlockId;

  // --- Event Handlers ---
  const handleLinkClick = (iri) => {
    if (!iri) return;
    if (iri.startsWith('artifact://')) {
      const [artifactId, ...blockIdParts] = iri.replace('artifact://', '').split('/');
      const blockIdWithoutPrefix = blockIdParts.length > 0 ? blockIdParts[blockIdParts.length - 1] : null;
      if (artifactId) {
        navigateToBlock(artifactId, blockIdWithoutPrefix);
      } else {
        console.error("Invalid internal IRI format (missing artifact ID):", iri);
      }
    } else if (iri.startsWith('http://') || iri.startsWith('https://')) {
      window.open(iri, '_blank', 'noopener,noreferrer');
    } else {
      console.warn("Non-navigable IRI:", iri);
    }
  };


  // --- Reusable Components (Header & Footer) ---

  const BlockHeader = ({ block }) => (
    // Simplified header, only Toolbar, more vertical margin
    <Toolbar blockGuid={block.guid} classes={"px-1 sm:px-2 py-1.5"} />
  );

  // New Refactored Footer Component
  const BlockFooter = ({ block, labelContentOverride = null, isDarkBg = false }) => { // Added isDarkBg prop
    const hasContent = block.type || block.createdAt || block.source || block.sources || block.label || labelContentOverride || block.path;
    if (!hasContent) return null; // Don't render empty footer

    // Define text colors based on background
    const textColor = isDarkBg ? 'text-gray-300' : 'text-gray-500';
    const labelValueColor = isDarkBg ? 'text-gray-100' : 'text-gray-700';
    const metaColor = isDarkBg ? 'text-gray-300' : 'text-gray-500';
    const pathSlugBg = isDarkBg ? 'bg-gray-500' : 'bg-gray-200/80';
    const editIconColor = isDarkBg ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-600';
    const footerBg = isDarkBg ? 'bg-gray-800/80 border-gray-700' : '';//'bg-gray-50/80 border-gray-200';


    const renderSourceLink = (source) => {
      if (!source || !source.attribution) return null;
      const attributionText = source.attribution || '(Unknown Source)';
      const linkColor = isDarkBg ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'; // Adjust link color for dark bg
      const commonClasses = `${linkColor} underline`;

      if (source.iri && source.iri.startsWith('artifact://')) {
        return (<span className={`cursor-pointer ${commonClasses}`} onClick={(e) => { e.stopPropagation(); handleLinkClick(source.iri); }} title={`Internal Link: ${source.iri}`}> {attributionText} </span>);
      } else if (source.iri && (source.iri.startsWith('http://') || source.iri.startsWith('https://'))) {
        return (<a href={source.iri} target="_blank" rel="noopener noreferrer" className={commonClasses} onClick={(e) => e.stopPropagation()} title={`External Link: ${source.iri}`}> {attributionText} <ExternalLink size={10} className="inline-block ml-0.5" /> </a>);
      } else {
        return <span title={source.iri ? `Source IRI (non-navigable): ${source.iri}` : 'Source Attribution'}>{attributionText}</span>;
      }
    };

    const typePart = block.type ? (
      <span className="footer-part type-part mr-3"> {/* Added margin */}
        Type: <span className={`font-medium ${labelValueColor}`}>{block.type}</span>
      </span>
    ) : null;

    const datePart = block.createdAt ? (
      <span className="footer-part date-part mr-3"> {/* Added margin */}
        Created: <span className={`font-medium ${labelValueColor}`}>{new Date(block.createdAt).toLocaleString()}</span>
      </span>
    ) : null;

    const sourcePart = block.source ? (
        <span className="footer-part source-part mr-3">Source: {renderSourceLink(block.source)}</span>
      ) : block.sources && block.sources.length > 0 ? (
        <span className="footer-part source-part mr-3">
          Source{block.sources.length > 1 ? 's' : ''}:{' '}
          {block.sources.map((source, index) => (
            <React.Fragment key={index}>
              {renderSourceLink(source)}
              {index < block.sources.length - 1 && ', '}
            </React.Fragment>
          ))}
        </span>
      ) : null;

    const labelText = labelContentOverride ?? block.label;
    const labelPart = labelText && labelText.trim() ? (
      <span className="footer-part label-part flex items-top mr-3"> {/* Added margin */}
        Label:{' '}
        <span className={`mx-1 min-h-[16px] rounded inline-block break-words px-1 font-medium ${labelValueColor} ${pathSlugBg}`}>{labelText}</span> {/* Use pathSlugBg for consistency */}
        <button className={`${editIconColor} opacity-50 hover:opacity-100 p-0.5 rounded flex-shrink-0 hidden md:inline-block`} disabled title="Edit Label (inactive)">
          <PencilLine size={12} />
        </button>
      </span>
    ) : null;

    const pathSegments = block.path ? block.path.split('/') : [];
    const pathPart = block.path ? (
       // Removed mr-3 here, as it's the last logical item
      <span className="footer-part path-part items-center flex-wrap hidden md:flex">
        <span className={`mr-1 flex-shrink-0 ${metaColor}`}><span class="path-label">Path:</span><span class="slug-label">Slug:</span></span>
        {pathSegments.map((segment, index) => (
          <React.Fragment key={index}>
            {index > 0 && <span className="text-gray-400 mx-0.5">/</span>}
            <span className={`${index === 0 ? 'pr-1' : 'px-1'} rounded inline-block break-words font-medium ${index === pathSegments.length - 1 ? `${labelValueColor} ${pathSlugBg} ml-1 inline-block px-1.5 min-h-[16px]` : metaColor}`} title={index === pathSegments.length - 1 ? 'Block Slug (Editable Placeholder)' : 'Path Segment'}>
              {segment}
            </span>
            {index === pathSegments.length - 1 && (
              <button className={`${isDarkBg ? 'text-gray-500 hover:text-gray-300':'text-gray-300 hover:text-gray-600'} ml-0.5 p-0.5 rounded align-middle hidden md:inline-block`} disabled title="Edit Slug (inactive)">
                <PencilLine size={12} />
              </button>
            )}
          </React.Fragment>
        ))}
      </span>
    ) : null;

    // Combine parts
    const footerParts = [typePart, datePart, sourcePart, labelPart, pathPart].filter(Boolean); // Filter out null parts

    return (
      <div className={`block-footer mt-auto pt-1.5 border-t ${footerBg} px-2 pb-1.5 text-xs rounded-b-md shadow-inner flex flex-wrap items-center gap-y-1 ${textColor}`}>
        {footerParts}
      </div>
    );
  };


  // --- Main Render Logic ---

  const contentPadding = "px-2 md:px-3 pb-2 pr-1 md:pr-2"; // Standard content padding

  switch (block.type) {
    case 'text':
      return (
        <div className={`block-type-text mb-2 bg-white border border-gray-200 rounded-md shadow-sm flex flex-col ${shouldBlink ? 'blink-target' : ''}`} id={fullBlockId} ref={handleSetRef}>
          <BlockHeader block={block} />
          <div className={`${contentPadding} flex-grow`}>
            <div className="text-sm">
              <ReactMarkdown>{block.content}</ReactMarkdown>
            </div>
            {block.children ? renderChildren(block.children, 0, blockRefs, renderingContext, referencingBlockId) : ''}
          </div>
          <BlockFooter block={block} />
        </div>
      );

    case 'heading':
      const HeadingTag = `h${block.level || 1}`;
      const headingClasses = ["text-xl lg:text-3xl font-bold mb-3", "text-xl lg:text-2xl font-bold mb-1", "text-lg lg:text-xl font-bold", "text-lg font-semibold", "text-base font-medium", "text-sm font-medium"];
      const headingClass = headingClasses[Math.min((block.level || 1) - 1, headingClasses.length - 1)];
      return (
        <div id={fullBlockId} className={`block-type-heading my-1 flex flex-col ${shouldBlink ? 'blink-target' : ''}`} ref={handleSetRef}>
          <BlockHeader block={block} />
            <HeadingTag className={`${headingClass} ${contentPadding} pt-0 pb-1 md:pl-2`}>{block.content}</HeadingTag>
          <BlockFooter block={block} labelContentOverride={block.content} />
        </div>
      );

    case 'section':
      return (
        <div className={`block-type-section mb-2 border border-gray-200 bg-gray-50/50 rounded-md shadow-sm flex flex-col ${shouldBlink ? 'blink-target' : ''}`} id={fullBlockId} ref={handleSetRef}>
          <BlockHeader block={block} />
          <div className={`block-content ${contentPadding} pt-1 flex-grow mb-1`}>
            {block.heading && (
              <Block block={{ ...block.heading, label: null }} renderChildren={renderChildren} setBlockRef={setBlockRef} blockRefs={blockRefs} renderingContext={renderingContext} referencingBlockId={referencingBlockId}/>
            )}
            {block.children ? renderChildren(block.children, 0, blockRefs, renderingContext, referencingBlockId) : <div className="text-sm text-gray-500 py-2">No content</div>}
          </div>
          <BlockFooter block={block} />
        </div>
      );

    case 'list':
      const isOrdered = block.ordered;
      const ListTag = isOrdered ? 'ol' : 'ul';
      return (
        <div className={`block-type-list-wrapper my-2 bg-white border border-gray-200 rounded-md shadow-sm flex flex-col ${shouldBlink ? 'blink-target' : ''}`} id={fullBlockId} ref={handleSetRef}>
          <BlockHeader block={block} />
          <div className={`${contentPadding} flex-grow`}>
            <ListTag className={`block-type-list ${isOrdered ? 'list-decimal' : 'list-disc'} ml-4 text-sm`}>
              {block.children ? block.children.map(child => ( <li key={child.id}> <Block block={child} renderChildren={renderChildren} setBlockRef={setBlockRef} blockRefs={blockRefs} renderingContext={renderingContext} referencingBlockId={referencingBlockId}/> </li> ))
               : <li className="text-sm text-gray-500">No items</li>}
            </ListTag>
          </div>
          <BlockFooter block={block} />
        </div>
      );

    case 'chatMessage':
      // Keep simple structure, add date to simple footer
      return (
        <div className={`block-type-chatmessage block-type-chatmessage-${block.sender === 'user' ? 'user' : 'ai'} pl-2 pr-1 md:pr-2 py-2 my-2 rounded-lg shadow-sm ${block.sender === 'user' ? 'bg-blue-100 ml-4 md:ml-12 mr-3' : 'bg-gray-200 mr-4 md:mr-12'} ${shouldBlink ? 'blink-target' : ''}`}
          id={fullBlockId} ref={handleSetRef}>
          <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
            <Toolbar blockGuid={block.guid} />
             {/* Date moved to footer */}
          </div>
          <div className="font-semibold mb-1 text-sm ml-2"> {block.sender === 'user' ? 'User' : 'AI'} </div>
          <div className="block-type-chatmessage-content text-sm pb-1">
            {block.children ? renderChildren(block.children, 0, blockRefs, renderingContext, referencingBlockId) : <div className="text-sm text-gray-500">Empty message</div>}
          </div>
           {/* Simple Date Footer for Chat Messages */}
           <div className="text-xs text-gray-500 text-right pr-1">
                {block.createdAt ? new Date(block.createdAt).toLocaleString() : ''}
           </div>
        </div>
      );

    case 'chat':
      // Use dark background
      return (
        <div className={`block-type-chat border border-gray-600 rounded-lg my-2 bg-gray-700 shadow-sm flex flex-col ${shouldBlink ? 'blink-target' : ''}`} id={fullBlockId} ref={handleSetRef}>
          <BlockHeader block={block} />
          <div className={`${contentPadding} pt-0 flex-grow`}>
            <h3 className={`text-lg lg:text-xl font-bold text-white ${contentPadding} pt-2 pb-1 pl-2 md:pl-2`}>{block.name}</h3>
            {block.children ? renderChildren(block.children, 0, blockRefs, renderingContext, referencingBlockId) : <div className="text-sm text-gray-400 py-2">No messages</div>} {/* Adjusted text color */}
            <div className="chat-controls flex items-center mt-4 border-t border-gray-500 pt-2"> {/* Adjusted border color */}
              <button className="ml-1 mr-2 bg-gray-500 text-gray-100 p-2 rounded-full hover:bg-gray-400" disabled title="Not implemented"> <PlusCircle size={16} /> </button>
              <input type="text" placeholder="Type your message here... (inactive in demo)" className="w-full p-2 border border-gray-500 bg-gray-600 text-gray-200 placeholder-gray-400 rounded-md text-sm" disabled /> {/* Adjusted input style */}
              <button className="ml-2 bg-gray-500 text-gray-100 px-3 py-2 mr-1 rounded-md hover:bg-gray-400" disabled title="Not implemented"> <Send size={20} /> </button>
            </div>
          </div>
          {/* Pass isDarkBg to footer */}
          <BlockFooter block={block} labelContentOverride={block.name} isDarkBg={true} />
        </div>
      );

    case 'lens':
      return (
        <div className={`block-type-lens border-2 border-purple-300 mb-2 bg-purple-50/80 rounded-md shadow-sm flex flex-col ${shouldBlink ? 'blink-target' : ''}`} id={fullBlockId} ref={handleSetRef}>
          <BlockHeader block={block} />
          <div className={`${contentPadding} flex-grow`}>
            <div className="text-sm font-medium text-purple-700 pb-2 ml-0 mt-0 flex items-center border-b border-purple-200">
              <Eye size={14} className="text-purple-600 mr-2 md:mr-1.5 flex-shrink-0" />
              <span>Prompt: {block.prompt}</span>
            </div>
            <div className="block-lens-result pt-2">{block.result ? renderChildren([block.result], 0, blockRefs, renderingContext, referencingBlockId) : <div className="text-sm text-gray-500">No result</div>}</div>
          </div>
          <BlockFooter block={block} />
        </div>
      );

    case 'reference':
      const [artifactIdRef, ...blockIdPartsRef] = (block.ref || '').replace('artifact://', '').split('/');
      const blockIdWithoutPrefixRef = blockIdPartsRef.length > 0 ? blockIdPartsRef[blockIdPartsRef.length - 1] : null;
      const referencedArtifact = getArtifactById(artifactIdRef);
      let referencedBlock = null;
      if (referencedArtifact && blockIdWithoutPrefixRef) { referencedBlock = findBlockById(artifactIdRef, blockIdWithoutPrefixRef); }
      const referencedContentContext = "reference";
      const referencingIdForContent = block.id;

      return (
        <div className={`block-type-reference my-2 bg-blue-50/80 border border-blue-200 rounded-md shadow-sm flex flex-col ${shouldBlink ? 'blink-target' : ''}`} id={fullBlockId} ref={handleSetRef}>
          <BlockHeader block={block} />
          <div className={`${contentPadding} flex-grow`}>
            <div className="text-xs mb-2">
              <span className="text-gray-500">Ref Link: </span>
              {block.ref ? (<span className="cursor-pointer text-blue-600 hover:text-blue-800 underline" onClick={(e) => { e.stopPropagation(); handleLinkClick(block.ref); }} title={`Internal Link: ${block.ref}`}> {block.label || block.ref} </span>)
                : (<span className="text-gray-400">(Invalid Reference IRI)</span>)}
            </div>
            <div className="block-reference-result mt-1 p-2 pr-1 md:pr-2 bg-gray-100 border-l-4 border-blue-300 rounded-md">
              {referencedBlock ? (<Block block={referencedBlock} renderChildren={renderChildren} setBlockRef={setBlockRef} blockRefs={blockRefs} renderingContext={referencedContentContext} referencingBlockId={referencingIdForContent} />)
                : referencedArtifact && !blockIdWithoutPrefixRef ? (renderChildren(referencedArtifact.children, 0, blockRefs, referencedContentContext, referencingIdForContent))
                  : (<div className="text-sm text-gray-500">Referenced content not found or invalid reference IRI.</div>)}
            </div>
          </div>
          <BlockFooter block={block} />
        </div>
      );

    default:
      // Unsupported block type
      return (
        <div className={`block-type-unsupported my-2 bg-red-50 border border-red-200 rounded-md shadow-sm flex flex-col ${shouldBlink ? 'blink-target' : ''}`} id={fullBlockId} ref={handleSetRef}>
          <BlockHeader block={block} />
          <div className={`${contentPadding} flex-grow`}>
            <div className="text-red-700 italic text-sm my-1">Unsupported block type: {block.type}</div>
          </div>
          <BlockFooter block={block} />
        </div>
      );
  }
};

export { Block };